import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import { ChevronLeft, Package, User, Layers, Info, Calendar, Database, HardDrive, Smartphone, Cpu } from 'lucide-react';
import './AdminPages.css';
import './AdminWorkspaceDetail.css'; // Reuse some detail styles

function AdminAppDetail() {
    const { id } = useParams();
    const [app, setApp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadApp();
    }, [id]);

    const loadApp = async () => {
        try {
            setLoading(true);
            const data = await adminManagementService.getAppDetail(id);
            setApp(data);
        } catch (err) {
            setError('Failed to load app details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="admin-loading">Loading app details...</div>;
    if (error) return <div className="admin-error">{error}</div>;
    if (!app) return <div className="admin-error">App consumed by the abyss</div>;

    return (
        <div className="admin-page">
            <div className="admin-breadcrumb">
                <Link to="/admin/apps">
                    <ChevronLeft size={16} />
                    Back to Apps
                </Link>
            </div>

            <div className="workspace-detail-header">
                <div className="header-with-icon">
                    <div className="icon-wrapper app-icon">
                        <Package size={32} />
                    </div>
                    <div>
                        <h1>{app.name}</h1>
                        <p className="workspace-id">ID: {app.id} • Version: {app.version || '1.0.0'}</p>
                    </div>
                </div>
                <span className={`status-badge ${app.platform === 'ios' ? 'info' : 'success'}`}>
                    {app.platform.toUpperCase()}
                </span>
            </div>

            <div className="detail-grid">
                {/* General Info */}
                <div className="detail-card">
                    <div className="card-header">
                        <Info size={20} />
                        <h3>General Information</h3>
                    </div>
                    <div className="card-body">
                        <div className="info-row">
                            <span className="info-label">Display Name:</span>
                            <span className="info-value">{app.name}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Platform:</span>
                            <span className="info-value text-uppercase">{app.platform}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Bundle ID:</span>
                            <span className="info-value">{app.bundleId || 'com.rova.app'}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Package Name:</span>
                            <span className="info-value">{app.packageName || 'com.rova.app'}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Total Builds:</span>
                            <span className="info-value">{app.buildsCount || 0}</span>
                        </div>
                    </div>
                </div>

                {/* Upload Context */}
                <div className="detail-card">
                    <div className="card-header">
                        <Calendar size={20} />
                        <h3>Upload Context</h3>
                    </div>
                    <div className="card-body">
                        <div className="info-row">
                            <span className="info-label">Uploaded By:</span>
                            <span className="info-value">
                                {app.uploader?.fullName} ({app.uploader?.email})
                            </span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Workspace:</span>
                            <span className="info-value">
                                <Link to={`/admin/workspaces/${app.workspaceId}`} className="admin-link">
                                    {app.workspace?.name}
                                </Link>
                            </span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Created At:</span>
                            <span className="info-value">{new Date(app.createdAt).toLocaleString()}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">File Size:</span>
                            <span className="info-value">{(app.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                    </div>
                </div>


            </div>
            {/* Binary / Storage Info */}
            <div className="detail-card">
                <div className="card-header">
                    <HardDrive size={20} />
                    <h3>Storage & Binary Details</h3>
                </div>
                <div className="card-body">
                    <div className="info-row vertical">
                        <span className="info-label">File Path / S3 Key:</span>
                        <code className="info-value code-block">{app.filePath}</code>
                    </div>
                    <div className="info-row vertical">
                        <span className="info-label">Checksum / Hash:</span>
                        <code className="info-value code-block">{app.checksum || app.hash || 'N/A'}</code>
                    </div>
                    {app.metadata && (
                        <div className="metadata-section">
                            <h4>Technical Metadata</h4>
                            <pre className="metadata-pre">
                                {JSON.stringify(app.metadata, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminAppDetail;
